import type { IInvoiceDataApiPort } from '@subscriptions/application/ports';
import type { FiscalRegimesDto, RetryInvoiceDataDto, RetryInvoiceScopeDto } from '@subscriptions/application/dtos';
import type { InvoiceData } from '@subscriptions/domain/entities';
import type { InvoiceDataHttpClientType } from '@subscriptions/infrastructure/api/InvoiceDataHttpClient';
import type { InvoiceDataApiMapperType } from '@subscriptions/infrastructure/mappers/InvoiceDataApiMapper';

/**
 * Creates the invoice data repository implementing IInvoiceDataApiPort.
 * Orchestrates HTTP client and mapper to fulfill application contracts.
 */
export const createInvoiceDataApiRepository = (props: {
  httpClient: InvoiceDataHttpClientType;
  mapper: InvoiceDataApiMapperType;
}): IInvoiceDataApiPort => {
  const { httpClient, mapper } = props;

  const create = async (invoiceData: InvoiceData): Promise<InvoiceData> => {
    const apiRequest = mapper.mapEntityToCreateApiRequest(invoiceData);
    const apiResponse = await httpClient.create(apiRequest);
    return mapper.mapApiResponseToEntity(apiResponse);
  };

  const update = async (invoiceData: InvoiceData, includeDeleted = false): Promise<InvoiceData> => {
    if (!invoiceData.uuid) {
      throw new Error('Invoice data uuid is required to update');
    }
    const apiRequest = mapper.mapEntityToUpdateApiRequest(invoiceData);
    const apiResponse = await httpClient.update(invoiceData.uuid, apiRequest, includeDeleted);
    return mapper.mapApiResponseToEntity(apiResponse);
  };

  const retry = async (paymentUuid: string, scope: RetryInvoiceScopeDto): Promise<RetryInvoiceDataDto> => {
    const apiResponse = await httpClient.retry(paymentUuid, mapper.mapRetryScopeToApiQuery(scope));
    return mapper.mapInvoiceActionApiResponseToDto(apiResponse);
  };

  const list = async (): Promise<InvoiceData[]> => {
    const apiResponse = await httpClient.list();
    return apiResponse.map(mapper.mapApiResponseToEntity);
  };

  const getById = async (invoiceDataUuid: string, includeDeleted = false): Promise<InvoiceData> => {
    const apiResponse = await httpClient.getById(invoiceDataUuid, includeDeleted);
    return mapper.mapApiResponseToEntity(apiResponse);
  };

  const deleteFn = async (invoiceDataUuid: string): Promise<void> => {
    await httpClient.delete(invoiceDataUuid);
  };

  const listFiscalRegimes = async (): Promise<FiscalRegimesDto> => {
    const apiResponse = await httpClient.listFiscalRegimes();
    return mapper.mapFiscalRegimesApiResponseToDto(apiResponse);
  };

  return { create, update, retry, list, getById, delete: deleteFn, listFiscalRegimes };
};
